import React, { useEffect, useMemo, useState } from "react";
import PartnerSpot from "./PartnerSpot";
import { LEVEL_STYLE, type Level } from "./IngredientSheet";
import { fill, findingsOf, nounFor, useLang, verdictLabel, verdictLevel } from "./i18n";
import { signal } from "./pulse";
import { WhyAdsLink } from "./KeepFree";
import { useMessages } from "./messages";

// API base MUST be /api/<appName> only. No fallback bases.
const API_BASE = "/api/bloom";
// One labelled ad row after every N products (unfiltered list only).
const AD_EVERY = 8;

export type Product = {
    asin: string;
    title: string;
    brand: string | null;
    imageUrl: string | null;
    price: { amount: number; currency: string; fetchedAt: string } | null;
    goUrl: string;
    // Same product, other sizes or colours share a family id (BloomCuratorService.group).
    family?: { id: string; brand: string | null; name: string | null; variant: string };
    ingredients: { overall: Level; counts: Record<Level, number>; total: number; names?: string[]; findings?: number; listKind?: string } | null;
    curation?: { category: string; certifications: string[]; ratings: number | null; why: string; items?: string[]; standard?: string; organic?: boolean } | null;
    // The verified ledger: once verified a product stays; the AI re-checks it on nextCheckAt.
    verified?: { at: string | null; lastCheckedAt: string | null; nextCheckAt: string | null; checks: number };
};

export type ProductsState =
    | { status: "loading" }
    | { status: "ready"; products: Product[] }
    | { status: "error"; error: string };

// Growth, in products: "New" marks a product verified in the last NEW_HOURS.
export const NEW_HOURS = 48;
export function verifiedWithin(p: Product, hours: number, now = Date.now()): boolean {
    const at = p.verified?.at ? new Date(p.verified.at).getTime() : NaN;
    return at > 0 && now - at < hours * 3600 * 1000;
}
export const isNew = (p: Product) => verifiedWithin(p, NEW_HOURS);

export type Sort = "all" | "least" | "most";
export type Mode = "contains" | "free";
export type Finder = { query: string; mode: Mode; sort: Sort; cat: string };

export function useProducts(lang: string): ProductsState {
    const [state, setState] = useState<ProductsState>({ status: "loading" });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/products?lang=${lang}`, { headers: { Accept: "application/json" } });
                const ctype = res.headers.get("content-type") || "";
                // HTML 200 is NOT success: it means a shell answered, not the API.
                if (!res.ok || !ctype.includes("application/json")) {
                    throw new Error(`GET ${API_BASE}/products returned ${res.status} ${ctype || "(no content-type)"}`);
                }
                const data = await res.json();
                if (!data.ok || !Array.isArray(data.products)) throw new Error(data.error || "unexpected response shape");
                if (!cancelled) setState({ status: "ready", products: data.products });
            } catch (e) {
                if (!cancelled) setState({ status: "error", error: e instanceof Error ? e.message : String(e) });
            }
        })();
        return () => { cancelled = true; };
    }, [lang]);
    return state;
}

// Lower-case and drop accents, so "creme" finds "Crème".
function norm(s: string) {
    return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}

// Contract rule C: findings = flagged items on the sheet (the server counts them; older rows fall back to counts).
const findings = (p: Product) => (p.ingredients ? (typeof p.ingredients.findings === "number" ? p.ingredients.findings : findingsOf(p.ingredients.counts)) : 0);

// The printed ingredient name that best matches the query (shortest containing it), or null.
function ingredientHit(p: Product, q: string): string | null {
    // Until the full sheet is ready, the curator's list (printed on the listing) answers the search.
    const names = p.ingredients?.names || p.curation?.items;
    if (!names || q.length < 3) return null;
    let best: string | null = null;
    for (const n of names) {
        if (norm(n).includes(q) && (!best || n.length < best.length)) best = n;
    }
    return best;
}

export type FinderResult = { list: { p: Product; hit: string | null }[]; ingredient: string | null };

export function applyFinder(all: Product[], f: Finder): FinderResult {
    const products = f.cat === "all" ? all
        : f.cat === "new" ? all.filter(isNew)
            : all.filter((p) => (p.curation?.category || "other") === f.cat);
    const q = norm(f.query);
    let ingredient: string | null = null;
    let list: { p: Product; hit: string | null }[];
    if (!q) {
        list = products.map((p) => ({ p, hit: null }));
    } else {
        const tokens = q.split(/\s+/).filter(Boolean);
        const hits = products.map((p) => ({ p, hit: ingredientHit(p, q) }));
        for (const h of hits) if (h.hit && (!ingredient || h.hit.length < ingredient.length)) ingredient = h.hit;
        if (ingredient && f.mode === "free") {
            // Only products we have actually read can be "free of" something.
            list = hits.filter((h) => h.p.ingredients && !h.hit).map((h) => ({ p: h.p, hit: null }));
        } else {
            list = hits.filter((h) => {
                const text = norm(h.p.title + " " + (h.p.brand || ""));
                return !!h.hit || tokens.every((tk) => text.includes(tk));
            });
        }
    }
    if (f.sort !== "all") {
        const ratio = (p: Product) => (p.ingredients && p.ingredients.total ? findings(p) / p.ingredients.total : 0);
        list = list.filter((x) => x.p.ingredients).sort((a, b) =>
            f.sort === "least"
                ? ratio(a.p) - ratio(b.p) || findings(a.p) - findings(b.p)
                : findings(b.p) - findings(a.p) || ratio(b.p) - ratio(a.p)
        );
    }
    return { list, ingredient };
}

// One card per family: the same product sold in several sizes or colours.
export type Group = { id: string; name: string | null; brand: string | null; members: Product[]; hits: (string | null)[] };

export function toGroups(list: { p: Product; hit: string | null }[]): Group[] {
    const byId: Record<string, Group> = {};
    const out: Group[] = [];
    for (const { p, hit } of list) {
        const id = p.family?.id || p.asin;
        let g = byId[id];
        if (!g) {
            g = byId[id] = { id, name: p.family?.name || null, brand: p.family?.brand || null, members: [], hits: [] };
            out.push(g);
        }
        g.members.push(p);
        g.hits.push(hit);
    }
    return out;
}

// The variant a card opens on: a search hit first, then one we have fully read, then the most rated.
function defaultIndex(g: Group): number {
    const hit = g.hits.findIndex(Boolean);
    if (hit >= 0) return hit;
    const score = (x: Product) => (x.ingredients ? 1e9 : 0) + (x.curation?.ratings || 0);
    let best = 0;
    g.members.forEach((m, i) => { if (score(m) > score(g.members[best])) best = i; });
    return best;
}

function MiniRing({ counts, total, center }: { counts: Record<Level, number>; total: number; center: React.ReactNode }) {
    const r = 15;
    const c = 2 * Math.PI * r;
    let offset = 0;
    return (
        <span className="relative inline-flex h-10 w-10 shrink-0">
            <svg viewBox="0 0 40 40" className="h-full w-full -rotate-90" aria-hidden="true">
                <circle cx="20" cy="20" r={r} fill="white" stroke="#e5e7eb" strokeWidth="5" />
                {(["high", "moderate", "unknown", "low"] as Level[]).map((lvl) => {
                    const n = counts[lvl] || 0;
                    if (!n || !total) return null;
                    const len = (n / total) * c;
                    const seg = (
                        <circle key={lvl} cx="20" cy="20" r={r} fill="none" stroke={LEVEL_STYLE[lvl].stroke} strokeWidth="5"
                            strokeDasharray={`${len} ${c}`} strokeDashoffset={-offset} />
                    );
                    offset += len;
                    return seg;
                })}
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[13px] font-extrabold text-gray-900">{center}</span>
        </span>
    );
}

// The hook of every card: the contract-C verdict. The catalog only lists products at step 0 (dark green)
// or 1 (light green); the same scale runs yellow to red in "Check a label".
function Verdict({ info, onOpen }: { info: NonNullable<Product["ingredients"]>; onOpen: () => void }) {
    const { t } = useLang();
    const n = typeof info.findings === "number" ? info.findings : findingsOf(info.counts);
    const v = verdictLevel(n);
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className={`group/v flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left ring-1 transition hover:shadow ${v.soft} ${v.ring}`}
        >
            <MiniRing counts={info.counts} total={info.total} center={n ? n : "✓"} />
            <span className="min-w-0 flex-1 leading-tight">
                <span className={`block text-[13px] font-extrabold leading-tight ${v.text}`}>{verdictLabel(t, n)}</span>
                <span className="block truncate text-[11px] text-gray-500">{fill(t.ofTotal, { total: info.total, noun: nounFor(t, info.total, info.listKind) })}</span>
            </span>
            <span className="text-lg leading-none text-gray-400 transition-transform group-hover/v:translate-x-0.5" aria-hidden="true">›</span>
        </button>
    );
}

function VariantPicker({ members, value, onChange }: { members: Product[]; value: number; onChange: (i: number) => void }) {
    const m = useMessages();
    const label = (p: Product, i: number) => p.family?.variant || fill(m.optionN, { n: i + 1 });
    const aria = fill(m.options, { n: members.length });
    if (members.length > 4) {
        return (
            <select
                value={value}
                aria-label={aria}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onChange(Number(e.target.value))}
                className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-800 focus:border-green-500 focus:outline-none"
            >
                {members.map((p, i) => <option key={p.asin} value={i}>{label(p, i)}</option>)}
            </select>
        );
    }
    return (
        <div role="radiogroup" aria-label={aria} className="mt-1.5 flex flex-wrap gap-1">
            {members.map((p, i) => (
                <button
                    key={p.asin}
                    type="button"
                    role="radio"
                    aria-checked={i === value}
                    onClick={(e) => { e.stopPropagation(); onChange(i); }}
                    className={
                        "max-w-full truncate rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 transition " +
                        (i === value ? "bg-gray-900 text-white ring-gray-900" : "bg-white text-gray-600 ring-gray-200 hover:ring-gray-400")
                    }
                >
                    {label(p, i)}
                </button>
            ))}
        </div>
    );
}

function shortDate(iso: string, lang: string) {
    try {
        return new Date(iso).toLocaleDateString(lang, { month: "short", day: "numeric" });
    } catch (e) {
        return iso.slice(0, 10);
    }
}

// When the AI verified this product and when it looks again: the ledger, in one line.
function VerifiedLine({ v }: { v: NonNullable<Product["verified"]> }) {
    const { lang } = useLang();
    const m = useMessages();
    if (!v.at) return null;
    return (
        <p className="mt-1 flex items-center gap-1 truncate text-[10px] text-gray-500">
            <span aria-hidden="true">🛡️</span>
            {fill(m.verifiedOn, { date: shortDate(v.at, lang) })}
            {v.nextCheckAt ? " · " + fill(m.nextCheck, { date: shortDate(v.nextCheckAt, lang) }) : ""}
        </p>
    );
}

// B30: each card links to its server-rendered page; a family's sizes and colours share the family's page.
const PAGE_PATH = "/api/bloom/p/";
function pageOf(p: Product, members: Product[]): string {
    const fam = p.family?.id;
    return fam && members.some((m) => m.asin === fam) ? fam : p.asin;
}

const STACKED = "mb-3 shadow-[0_5px_0_-1px_#fff,0_5px_0_0_#bbf7d0,0_10px_0_-2px_#fff,0_10px_0_-1px_#dcfce7] ";

function ProductCard({ group, showHit, onIngredients }: { group: Group; showHit: boolean; onIngredients: (asin: string) => void }) {
    const { t, lang } = useLang();
    const m = useMessages();
    const [picked, setPicked] = useState(() => defaultIndex(group));
    const i = Math.min(picked, group.members.length - 1);
    const product = group.members[i];
    const hit = showHit ? group.hits[i] : null;
    const brand = group.brand || product.brand;
    const heading = group.name || product.title;
    const open = product.ingredients ? () => onIngredients(product.asin) : undefined;
    const many = group.members.length > 1;
    const fresh = group.members.some(isNew);
    return (
        <div
            onClick={open}
            className={
                "group relative flex min-h-[11.5rem] overflow-hidden rounded-2xl border border-gray-100 bg-white transition-all duration-300 hover:-translate-y-0.5 " +
                // Several verified products in one card: drawn as a stack, so the count reads at a glance.
                (many ? STACKED : "shadow-sm hover:shadow-xl ") +
                (open ? "cursor-pointer" : "")
            }
        >
            {fresh ? (
                <span className="absolute left-0 top-0 z-10 rounded-br-xl bg-emerald-600 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow">
                    {m.newBadge}
                </span>
            ) : null}
            {product.imageUrl ? (
                <div className="flex w-28 shrink-0 items-center justify-center border-r border-gray-50 bg-white p-2 sm:w-32">
                    <img
                        src={product.imageUrl}
                        alt={product.title}
                        loading="lazy"
                        className="max-h-40 w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                </div>
            ) : null}
            <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
                <div className="min-w-0">
                    {brand ? <p className="truncate text-[10px] font-bold uppercase tracking-wider text-green-700">{brand}</p> : null}
                    <h3 className="line-clamp-2 text-sm font-bold leading-snug text-gray-900" title={product.title}>
                        {/* B30: a real link to the product's own page, for crawlers and new tabs; a plain tap still opens the sheet. */}
                        <a
                            href={PAGE_PATH + pageOf(product, group.members) + (lang === "en" ? "" : "?lang=" + lang)}
                            onClick={(e) => {
                                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) { e.stopPropagation(); return; }
                                if (open) e.preventDefault();
                            }}
                            className="hover:underline"
                        >
                            {heading}
                        </a>
                    </h3>
                    {product.curation ? <Credentials c={product.curation} /> : null}
                    {product.verified ? <VerifiedLine v={product.verified} /> : null}
                    {many ? (
                        <>
                            <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-extrabold text-green-800">
                                <span aria-hidden="true">🛡️</span>{fill(m.familyCount, { n: group.members.length })}
                            </p>
                            <VariantPicker members={group.members} value={i} onChange={setPicked} />
                        </>
                    ) : null}
                    {hit ? (
                        <p className="mt-1 truncate text-[11px] font-semibold text-violet-700">
                            {t.contains}: <span className="rounded bg-violet-50 px-1">{hit}</span>
                        </p>
                    ) : null}
                </div>
                <div className="mt-auto space-y-2">
                    {product.ingredients ? (
                        <Verdict info={product.ingredients} onOpen={() => onIngredients(product.asin)} />
                    ) : product.curation?.items?.length ? (
                        <p className="line-clamp-2 text-[11px] font-medium text-gray-600">{product.curation.items.join(" · ")}</p>
                    ) : (
                        <p className="text-[11px] text-gray-400">{t.noList}</p>
                    )}
                    <a
                        href={product.goUrl}
                        onClick={(e) => { e.stopPropagation(); signal("amazon_tap", { a: product.asin }); }}
                        target="_blank"
                        rel="sponsored nofollow noopener noreferrer"
                        data-obto-cta="amazon"
                        className="block rounded-lg bg-green-600 px-2 py-1.5 text-center text-xs font-semibold text-white transition hover:bg-green-700"
                    >
                        {t.checkPrice}
                    </a>
                </div>
            </div>
        </div>
    );
}

// Why it made the cut: certifications printed on the listing, and how many buyers rated it.
function Credentials({ c }: { c: NonNullable<Product["curation"]> }) {
    const { t } = useLang();
    const certs = c.certifications.slice(0, 2);
    const k = c.ratings ? (c.ratings >= 10000 ? Math.round(c.ratings / 1000) + "k" : c.ratings.toLocaleString()) : null;
    return (
        <p className="mt-1.5 flex flex-wrap items-center gap-1" title={c.why}>
            {certs.map((x) => (
                <span key={x} className="inline-flex items-center gap-1 rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-bold text-white">
                    ✓ {x}
                </span>
            ))}
            {c.standard === "clean" ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-800 ring-1 ring-emerald-200">{t.cleanFormula}</span> : null}
            {k ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-100">★ {fill(t.ratingsShort, { n: k })}</span> : null}
        </p>
    );
}

function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; label: string }) {
    return (
        <div role="radiogroup" aria-label={label} className="flex shrink-0 gap-1 rounded-full bg-gray-100 p-1">
            {options.map((o) => (
                <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={value === o.id}
                    onClick={() => onChange(o.id)}
                    className={
                        "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition " +
                        (value === o.id ? "bg-white text-gray-900 shadow" : "text-gray-500 hover:text-gray-800")
                    }
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

type GridProps = {
    state: ProductsState;
    finder: Finder;
    onFinder: (f: Finder) => void;
    onIngredients: (asin: string) => void;
    onCheck: () => void;
};

const CAT_ICON: Record<string, string> = {
    all: "🌱", food: "🥗", drink: "🍵", supplement: "🌿", personal_care: "🧴", baby: "👶",
    household: "🏠", textile: "🛏️", furniture: "🪑", other: "✨",
};

// Shelf buttons in the sticky header, right under the search box. Counts are cards (one product
// type each, contract rule 8), never sizes or colours; "New" appears once part of the shelf is
// older than NEW_HOURS.
export function CategoryBar({ products, value, onChange }: { products: Product[]; value: string; onChange: (c: string) => void }) {
    const { t } = useLang();
    const m = useMessages();
    const groups = toGroups(products.map((p) => ({ p, hit: null })));
    const counts: Record<string, number> = {};
    for (const g of groups) {
        const c = g.members[0].curation?.category || "other";
        counts[c] = (counts[c] || 0) + 1;
    }
    const total = groups.length;
    const fresh = groups.filter((g) => g.members.some(isNew)).length;
    const cats = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
    if (!cats.length) return null;
    const chip = (id: string, label: string, n: number) => (
        <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            aria-pressed={value === id}
            className={
                "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ring-1 transition " +
                (value === id ? "bg-green-700 text-white ring-green-700 shadow" : "bg-white text-gray-700 ring-green-100 hover:ring-green-300")
            }
        >
            <span aria-hidden="true">{id === "new" ? "✨" : CAT_ICON[id] || CAT_ICON.other}</span>
            {label}
            <span className={value === id ? "text-white/70" : "text-gray-400"}>{n}</span>
        </button>
    );
    return (
        <nav aria-label={t.cat.all} className="-mx-4 mt-2 flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] sm:-mx-6 sm:px-6 [&::-webkit-scrollbar]:hidden">
            {chip("all", t.cat.all, total)}
            {fresh && fresh < total ? chip("new", m.catNew, fresh) : null}
            {cats.map((c) => chip(c, t.cat[c] || c, counts[c]))}
        </nav>
    );
}

export default function ProductGrid({ state, finder, onFinder, onIngredients, onCheck }: GridProps) {
    const { t } = useLang();
    const m = useMessages();
    const result = useMemo(
        () => (state.status === "ready" ? applyFinder(state.products, finder) : { list: [], ingredient: null }),
        [state, finder]
    );
    const groups = useMemo(() => toGroups(result.list), [result]);
    const totalGroups = useMemo(
        () => (state.status === "ready" ? toGroups(state.products.map((p) => ({ p, hit: null }))).length : 0),
        [state]
    );

    if (state.status === "loading") {
        return (
            <main className="flex-1 p-4 sm:p-6">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 2xl:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-44 animate-pulse rounded-2xl bg-gray-100" />)}
                </div>
                <p className="sr-only">{t.loading}</p>
            </main>
        );
    }

    if (state.status === "error") {
        return (
            <main className="flex-1 p-6">
                <p className="text-red-700">{t.loadError}</p>
                <p className="mt-1 text-xs text-gray-400">{state.error}</p>
            </main>
        );
    }

    const filtering = !!finder.query.trim() || finder.sort !== "all" || finder.cat !== "all";
    const cells: React.ReactNode[] = [];
    groups.forEach((g, i) => {
        if (!filtering && i > 0 && i % AD_EVERY === 0) cells.push(<PartnerSpot key={`ad-${i}`} variant="infeed" />);
        cells.push(<ProductCard key={g.id} group={g} showHit={finder.mode === "contains"} onIngredients={onIngredients} />);
    });

    return (
        <main id="products" className="min-w-0 flex-1 scroll-mt-44 p-4 sm:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-base font-extrabold text-gray-900">
                        {filtering
                            ? fill(m.resultsProducts, { n: groups.length, total: totalGroups })
                            : fill(m.gridLead, { n: totalGroups })}
                    </p>
                    {state.products.length > totalGroups ? (
                        <p className="text-xs text-gray-500">{fill(m.variantsCount, { n: state.products.length })} · {m.gridStacked}</p>
                    ) : null}
                </div>
                <Segmented
                    label={t.sortLabel}
                    value={finder.sort}
                    onChange={(sort) => onFinder({ ...finder, sort })}
                    options={[
                        { id: "all", label: t.all },
                        { id: "least", label: t.sortLeast },
                        { id: "most", label: t.sortMost },
                    ]}
                />
            </div>
            {result.ingredient ? (
                <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl bg-violet-50 px-3 py-2 ring-1 ring-violet-100 animate-in fade-in duration-200">
                    <Segmented
                        label={result.ingredient}
                        value={finder.mode}
                        onChange={(mode) => onFinder({ ...finder, mode })}
                        options={[
                            { id: "contains", label: t.contains },
                            { id: "free", label: t.freeOf },
                        ]}
                    />
                    <span className="truncate text-sm font-bold text-violet-800">{result.ingredient}</span>
                </div>
            ) : null}
            <p className="mb-4 text-xs text-gray-400">{t.disclosure} <WhyAdsLink /></p>
            {groups.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 p-8 text-center">
                    <p className="text-sm text-gray-600">{state.products.length ? t.noMatch : t.empty}</p>
                    <button
                        type="button"
                        onClick={onCheck}
                        className="mt-4 rounded-xl bg-green-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-green-700"
                    >
                        {t.checkLabel}
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 2xl:grid-cols-3">{cells}</div>
            )}
        </main>
    );
}
